import { apiErrorCode, apiErrorRef, extractApiError } from "./apiErrors";

/**
 * Map a failed billing-page Stripe call to a sentence the practitioner can read, in their
 * own language, quoting the support reference — the billing-page mirror of
 * lib/catalogueErrors.js.
 *
 * The backend answers `{"detail": {"code", "ref", "message"}}` (backend/stripe_refusal.py)
 * from change-plan, the Stripe portal button and the booster / package checkouts. The CODE
 * is the contract; `message` is an English developer diagnostic and must never reach the
 * toast (R5-B2: the toast used to be Stripe's own English prose, with `prod_…` / `price_…`
 * ids). `ref` is what support needs to find the call, so every sentence quotes it.
 *
 * Imported relatively (not through "@/lib/api"): apiErrors.js is the pure module, and
 * api.js re-exports only `extractApiError` from it.
 *
 * Pure, and takes `t` — a translator bound to the "admin" namespace, where the billing
 * page's `bill.*` strings live — as an argument.
 *
 * @param {object} e         axios error
 * @param {function} t       translator bound to the "admin" namespace
 * @param {string} fallback  screen-level generic message
 * @returns {string}
 */
export function billingErrorMessage(e, t, fallback = "") {
  const key = BILLING_ERROR_KEYS[apiErrorCode(e)];
  if (key) return t(key, { ref: apiErrorRef(e) });
  return extractApiError(e, fallback) || fallback;
}

// One entry per code of backend/stripe_refusal.py that the billing page can display.
// PURCHASE_STATUS_UNAVAILABLE is deliberately absent: the post-Checkout status poll
// (PurchaseCatalogCard) is silent on failure, so no screen shows it.
const BILLING_ERROR_KEYS = {
  // Stripe deterministically refused the re-price: nothing changed on either side.
  PLAN_CHANGE_REFUSED: "bill.err.planChangeRefused",
  // Network cut / Stripe 5xx: the re-price MAY have gone through; the server holds the
  // change lock for a few minutes and wrote nothing locally.
  PLAN_CHANGE_OUTCOME_UNKNOWN: "bill.err.planChangeOutcomeUnknown",
  BILLING_PORTAL_UNAVAILABLE: "bill.err.portalUnavailable",
  // Booster and package checkouts share both codes.
  PURCHASE_CHECKOUT_REFUSED: "bill.err.checkoutRefused",
  PURCHASE_CHECKOUT_OUTCOME_UNKNOWN: "bill.err.checkoutOutcomeUnknown",
  // An operator price change (or another plan change) holds the billing-change lock on the
  // practice's subscription; nothing was written, and a retry in a few minutes is safe.
  BILLING_CHANGE_IN_PROGRESS: "bill.err.billingChangeInProgress",
  // A newer billing change took the lock over between this change's claim and its commit:
  // nothing of this one was recorded, and the page re-reads what is now in force.
  PLAN_CHANGE_SUPERSEDED: "bill.err.planChangeSuperseded",
  // The practice is being deleted (D7 contract 3.8): no booster or package checkout opens.
  PRACTICE_DELETION_PENDING: "bill.err.practiceDeletionPending",
};
